import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Key,
  People,
  PersonAdd,
  Visibility,
  VisibilityOff,
  Block,
  CheckCircle,
  Star,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  FilterBar,
  CorporateTable,
  EmptyState,
  ActionButtonGroup,
  CorporateDialog,
  SkeletonLoader,
  CorporateBadge,
} from '../../../components/ui';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { User, Role } from '../../../types';
import { validatePassword } from '../../../utils/passwordValidator';
import { PasswordRequirements } from '../../../components/PasswordRequirements';
import { logger } from '../../../utils/logger';

export const UsersManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page] = useState(1);
  const [total, setTotal] = useState(0);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role_id: '',
    locale: 'es',
    avatar_url: '',
    background_image: '1',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));

  const [editUser, setEditUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role_id: '',
    locale: 'es',
    avatar_url: '',
    background_image: '1',
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetPasswordValidation, setResetPasswordValidation] = useState(validatePassword(''));
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page, search]);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers({
        q: search || undefined,
        page,
        per_page: 20,
      });
      setUsers(response.items || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      logger.error('Error cargando usuarios:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar usuarios';
      showError(errorMessage);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesData = await apiService.getRoles();
      setRoles(rolesData);
    } catch (error: any) {
      logger.error('Error cargando roles:', error);
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.email || !newUser.password || !newUser.role_id) {
        showError(t('profile.completeAllFields'));
        return;
      }
      const validation = validatePassword(newUser.password);
      if (!validation.isValid) {
        showError(validation.errors.join('; '));
        return;
      }
      await apiService.createUser(newUser);
      showSuccess(t('admin.userCreated'));
      setOpenUserDialog(false);
      resetNewUserForm();
      loadUsers();
    } catch (error: any) {
      showError(error.response?.data?.detail || error.message || t('admin.loadingError'));
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    try {
      await apiService.updateUser(selectedUser.id, editUser);
      showSuccess(t('admin.userUpdated'));
      setOpenEditDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      showError(error.response?.data?.detail || error.message || t('admin.loadingError'));
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      showError(validation.errors.join('; '));
      return;
    }
    if (newPassword !== confirmPassword) {
      showError(t('auth.passwordMismatch'));
      return;
    }
    try {
      await apiService.forcePasswordReset(selectedUser.id, newPassword);
      showSuccess(t('admin.passwordResetSuccess'));
      setOpenPasswordDialog(false);
      setNewPassword('');
      setConfirmPassword('');
      setResetPasswordValidation(validatePassword(''));
      setShowResetPassword(false);
      setShowResetConfirmPassword(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      showError(error.response?.data?.detail || error.message || t('admin.loadingError'));
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await apiService.deleteUser(selectedUser.id);
      showSuccess(t('admin.userDeleted'));
      setOpenDeleteDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      showError(error.response?.data?.detail || error.message || 'Error al eliminar usuario');
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await apiService.deactivateUser(userId);
      loadUsers();
    } catch (error) {
      logger.error('Error desactivando usuario:', error);
    }
  };

  const handleReactivate = async (userId: string) => {
    try {
      await apiService.reactivateUser(userId);
      loadUsers();
    } catch (error) {
      logger.error('Error reactivando usuario:', error);
    }
  };

  const handlePromote = async (userId: string) => {
    try {
      await apiService.promoteUserToAdmin(userId);
      loadUsers();
    } catch (error) {
      logger.error('Error promoviendo usuario:', error);
    }
  };

  const resetNewUserForm = () => {
    setNewUser({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role_id: '',
      locale: 'es',
      avatar_url: '',
      background_image: '1',
    });
    setShowPassword(false);
    setPasswordValidation(validatePassword(''));
  };

  const openEditUserDialog = (user: User) => {
    setSelectedUser(user);
    setEditUser({
      email: user.email,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role_id: user.role_id,
      locale: user.locale || 'es',
      avatar_url: user.avatar_url || '',
      background_image: user.background_image || '1',
    });
    setOpenEditDialog(true);
  };

  const openResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordValidation(validatePassword(''));
    setShowResetPassword(false);
    setShowResetConfirmPassword(false);
    setOpenPasswordDialog(true);
  };

  const openDeleteUserDialog = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const getBackgroundImageName = (bgId: string | null) => {
    if (!bgId) return '1';
    const bgNames: { [key: string]: string } = {
      '1': 'Bosque',
      '2': 'Montaña',
      '3': 'Valle',
    };
    return bgNames[bgId] || bgId;
  };

  const tableHeaders = useMemo(
    () => ['Email', 'Nombre', 'Rol', 'Estado', 'Verificado', 'Imagen Fondo', 'Acciones'],
    [],
  );

  return (
    <Box>
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('common.search')}
        resultCount={users.length}
        totalCount={total || users.length}
        additionalActions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetNewUserForm();
              setOpenUserDialog(true);
            }}
          >
            {t('admin.addUser')}
          </Button>
        }
      />

      {loading && users.length === 0 && <SkeletonLoader variant="table" count={5} columns={7} />}

      {!loading && users.length === 0 && (
        <EmptyState
          icon={<People />}
          title={t('admin.noUsers')}
          description="Comienza agregando tu primer usuario a la plataforma"
          actionLabel={t('admin.addUser')}
          onAction={() => {
            resetNewUserForm();
            setOpenUserDialog(true);
          }}
        />
      )}

      {!loading && users.length > 0 && (
        <CorporateTable headers={tableHeaders} zebraStriping gradientHeader hoverRows>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.display_name}</TableCell>
              <TableCell>
                <CorporateBadge
                  variant="role"
                  type={
                    user.role_name === 'super_admin'
                      ? 'role-super_admin'
                      : user.role_name === 'admin'
                      ? 'role-admin'
                      : 'role-user'
                  }
                  label={user.role_name || 'Sin rol'}
                  showIcon
                  gradient
                  shadow
                />
              </TableCell>
              <TableCell>
                <CorporateBadge
                  variant="status"
                  type={user.is_active ? 'status-active' : 'status-inactive'}
                  label={user.is_active ? 'Activo' : 'Inactivo'}
                  showIcon
                  gradient
                  shadow
                />
              </TableCell>
              <TableCell>
                <CorporateBadge
                  variant="verified"
                  type={user.is_verified ? 'verified-yes' : 'verified-no'}
                  label={user.is_verified ? 'Sí' : 'No'}
                  showIcon
                  gradient
                  shadow
                />
              </TableCell>
              <TableCell>
                <CorporateBadge
                  variant="image"
                  type="image"
                  label={getBackgroundImageName(user.background_image)}
                  showIcon
                  gradient={false}
                  shadow
                />
              </TableCell>
              <TableCell>
                <ActionButtonGroup
                  actions={[
                    {
                      icon: <Edit />,
                      tooltip: 'Editar usuario',
                      onClick: () => openEditUserDialog(user),
                      color: 'primary',
                    },
                    {
                      icon: <Key />,
                      tooltip: 'Resetear contraseña',
                      onClick: () => openResetPasswordDialog(user),
                      color: 'warning',
                    },
                    {
                      icon: user.is_active ? <Block /> : <CheckCircle />,
                      tooltip: user.is_active ? 'Desactivar usuario' : 'Activar usuario',
                      onClick: () =>
                        user.is_active ? handleDeactivate(user.id) : handleReactivate(user.id),
                      color: user.is_active ? 'error' : 'success',
                    },
                    ...(user.role_name !== 'admin' && user.role_name !== 'super_admin'
                      ? [
                          {
                            icon: <Star />,
                            tooltip: 'Promover a administrador',
                            onClick: () => handlePromote(user.id),
                            color: 'warning' as const,
                          },
                        ]
                      : []),
                    {
                      icon: <Delete />,
                      tooltip: 'Eliminar usuario',
                      onClick: () => openDeleteUserDialog(user),
                      color: 'error',
                      destructive: true,
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
        </CorporateTable>
      )}

      <CorporateDialog
        open={openUserDialog}
        onClose={() => setOpenUserDialog(false)}
        title="Crear Nuevo Usuario"
        icon={<PersonAdd />}
        maxWidth="sm"
        fullWidth
        actions={
          <>
            <Button onClick={() => setOpenUserDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateUser} variant="contained">
              {t('admin.addUser')}
            </Button>
          </>
        }
      >
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contraseña *"
              type={showPassword ? 'text' : 'password'}
              value={newUser.password}
              onChange={(e) => {
                setNewUser({ ...newUser, password: e.target.value });
                setPasswordValidation(validatePassword(e.target.value));
              }}
              error={newUser.password.length > 0 && !passwordValidation.isValid}
              helperText={
                newUser.password.length > 0 && !passwordValidation.isValid
                  ? passwordValidation.errors[0]
                  : t('auth.passwordMinLengthHelper')
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: newUser.password.length > 0 ? 1 : 0,
              }}
            />
          </Grid>
          {newUser.password.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ mb: 1 }}>
                <PasswordRequirements validation={passwordValidation} />
              </Box>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Nombre"
              value={newUser.first_name}
              onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Apellidos"
              value={newUser.last_name}
              onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Rol *</InputLabel>
              <Select
                value={newUser.role_id}
                onChange={(e) => setNewUser({ ...newUser, role_id: e.target.value })}
                label="Rol *"
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Idioma</InputLabel>
              <Select
                value={newUser.locale}
                onChange={(e) => setNewUser({ ...newUser, locale: e.target.value })}
                label="Idioma"
              >
                <MenuItem value="es">Español</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ca">Català</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Imagen de Fondo</InputLabel>
              <Select
                value={newUser.background_image}
                onChange={(e) => setNewUser({ ...newUser, background_image: e.target.value })}
                label="Imagen de Fondo"
              >
                <MenuItem value="1">Bosque</MenuItem>
                <MenuItem value="2">Montaña</MenuItem>
                <MenuItem value="3">Valle</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL Avatar"
              value={newUser.avatar_url}
              onChange={(e) => setNewUser({ ...newUser, avatar_url: e.target.value })}
              placeholder="https://..."
            />
          </Grid>
        </Grid>
      </CorporateDialog>

      <CorporateDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        title="Editar Usuario"
        icon={<Edit />}
        maxWidth="sm"
        fullWidth
        actions={
          <>
            <Button onClick={() => setOpenEditDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleEditUser} variant="contained">
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editUser.email}
              onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Nombre"
              value={editUser.first_name}
              onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Apellidos"
              value={editUser.last_name}
              onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select
                value={editUser.role_id}
                onChange={(e) => setEditUser({ ...editUser, role_id: e.target.value })}
                label="Rol"
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Idioma</InputLabel>
              <Select
                value={editUser.locale}
                onChange={(e) => setEditUser({ ...editUser, locale: e.target.value })}
                label="Idioma"
              >
                <MenuItem value="es">Español</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ca">Català</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Imagen de Fondo</InputLabel>
              <Select
                value={editUser.background_image}
                onChange={(e) => setEditUser({ ...editUser, background_image: e.target.value })}
                label="Imagen de Fondo"
              >
                <MenuItem value="1">Bosque</MenuItem>
                <MenuItem value="2">Montaña</MenuItem>
                <MenuItem value="3">Valle</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL Avatar"
              value={editUser.avatar_url}
              onChange={(e) => setEditUser({ ...editUser, avatar_url: e.target.value })}
              placeholder="https://..."
            />
          </Grid>
        </Grid>
      </CorporateDialog>

      <CorporateDialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        title="Resetear Contraseña"
        icon={<Key />}
        maxWidth="sm"
        fullWidth
        actions={
          <>
            <Button onClick={() => setOpenPasswordDialog(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={!newPassword || !confirmPassword || !resetPasswordValidation.isValid || newPassword !== confirmPassword}
            >
              {t('admin.forcePasswordReset')}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          Usuario: {selectedUser?.email}
        </Typography>
        <TextField
          fullWidth
          label="Nueva Contraseña"
          type={showResetPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setResetPasswordValidation(validatePassword(e.target.value));
          }}
          error={newPassword.length > 0 && !resetPasswordValidation.isValid}
          helperText={
            newPassword.length > 0 && !resetPasswordValidation.isValid
              ? resetPasswordValidation.errors[0]
              : t('auth.passwordMinLengthHelper')
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  edge="end"
                >
                  {showResetPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: newPassword.length > 0 ? 1 : 2 }}
        />
        {newPassword.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <PasswordRequirements validation={resetPasswordValidation} />
          </Box>
        )}
        <TextField
          fullWidth
          label="Confirmar Contraseña"
          type={showResetConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword.length > 0 && newPassword !== confirmPassword}
          helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? t('auth.passwordMismatch') : ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                  edge="end"
                >
                  {showResetConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </CorporateDialog>

      <CorporateDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="Eliminar Usuario"
        icon={<Delete />}
        gradientHeader={false}
        actions={
          <>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
            <Button onClick={handleDeleteUser} variant="contained" color="error">
              Eliminar
            </Button>
          </>
        }
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          ¿Estás seguro de que deseas eliminar al usuario <strong>{selectedUser?.email}</strong>?
          Esta acción no se puede deshacer.
        </Alert>
      </CorporateDialog>
    </Box>
  );
};

